package com.bikompanion.ui.models

import com.bikompanion.domain.BikeIssue
import com.bikompanion.domain.BikeRatingScore
import com.bikompanion.domain.BikeScopeSource
import com.bikompanion.domain.NetworkScopeCandidate
import com.bikompanion.ui.mapper.BikeScoreSummaryUiModel

/**
 * What the rate-a-bike dialog shows.
 *
 * Tapping "Bad" doesn't submit right away — [isReportingIssue] switches the dialog into
 * picking a reason first, which [selectedIssues] collects until the report is confirmed.
 */
data class BikeRatingUiState(
    val bikeNumber: String = "",
    val isElectric: Boolean = false,
    val selectedVote: Boolean? = null,
    val selectedIssues: Set<BikeIssue> = emptySet(),
    /**
     * The networks the rider could plausibly be standing in, nearest first.
     *
     * Bike numbers only mean anything inside a network — bike 1234 in Bicing and bike 1234 in
     * Vélib' are different bikes — so nothing can be looked up or submitted until one is chosen.
     */
    val networkCandidates: List<NetworkScopeCandidate> = emptyList(),
    /** Which of [networkCandidates] the vote will be filed under, once there is an answer. */
    val selectedNetworkId: String? = null,
    /** How [selectedNetworkId] was arrived at, recorded on the vote itself. */
    val scopeSource: BikeScopeSource = BikeScopeSource.ManualPick,
    /**
     * The looked-up score for [bikeNumber], or null while nothing has been looked up yet.
     *
     * A score with `totalCount == 0` is a real answer — that bike simply has no votes — so the
     * screen can tell "nobody has rated this" apart from "we haven't checked".
     */
    val score: BikeRatingScore? = null,
    /** [score] shaped for the dialog to show in one line, or null alongside it. */
    val scoreSummary: BikeScoreSummaryUiModel? = null,
    /** True while the typed number's score is being fetched, so the field can show it. */
    val isCheckingScore: Boolean = false,
    val statusMessage: BikeRatingMessage? = null,
    val isSubmitting: Boolean = false,
    /** A one-shot success message for the screen to toast, then clear via [com.bikompanion.ui.home.HomeScreenActions.ToastShown]. */
    val toastMessage: BikeRatingMessage? = null,
) {
    val isReportingIssue: Boolean get() = selectedVote == false

    /**
     * Whether the rider still has to say which network the bike belongs to.
     *
     * Several networks in one city is the normal case — Barcelona runs Bicing *and* AMBici with
     * independent numbering — so this is a question to ask, not an error to report.
     */
    val needsNetworkChoice: Boolean get() = selectedNetworkId == null && networkCandidates.isNotEmpty()

    /**
     * Whether asking for a bike number makes sense yet.
     *
     * The dialog reveals itself a step at a time — a number typed before a network is settled
     * can neither be looked up nor submitted, so it isn't asked for until it can be answered.
     */
    val canEnterBikeNumber: Boolean get() = selectedNetworkId != null

    /** Whether there is a real bike to say something about, so the vote can be offered. */
    val canVote: Boolean get() = canEnterBikeNumber && bikeNumber.isNotBlank()

    /** Whether the Submit button should accept a tap — mirrors the guard in `onSubmitClick`. */
    val canSubmit: Boolean get() = canVote && selectedVote != null && !isSubmitting

    /** The network the vote would be filed under, if one has been settled on. */
    val selectedNetwork: NetworkScopeCandidate?
        get() = networkCandidates.firstOrNull { it.network.id == selectedNetworkId }
}
